/**
 * Represents the configuration for a single report.
 */
export interface ReportConfig {
  /** The text to be displayed as the header for this report. */
  headerText: string;
  /** The unique identifier for the QuickSight dashboard associated with this report. */
  dashboardId: string;
  /** Whether this report should be rendered as a button link instead of an expandable section. */
  renderAsButton?: boolean;
}

/**
 * Represents the configuration for a tab in the report page.
 */
export interface TabConfig {
  /** The label to be displayed for this tab. */
  label: string;
  /** A unique identifier for this tab. */
  id: string;
  /** An array of ReportConfig objects representing the reports to be displayed in this tab. */
  reports: ReportConfig[];
}

/**
 * Represents the configuration for an entire report page.
 */
export interface ReportPageConfig {
  /** The AWS account ID associated with the QuickSight dashboards. */
  accountId: string;
  /** The directory alias used in the QuickSight dashboard URLs. */
  directoryAlias: string;
  /** The AWS region where the QuickSight dashboards are hosted. */
  region: string;
  /** An array of TabConfig objects representing the tabs to be displayed on this report page. Optional. */
  tabs?: TabConfig[];
  /** An array of ReportConfig objects representing the reports to be displayed directly on this page when no tabs are present. Optional. */
  reports?: ReportConfig[];
}

/**
 * A record of report page configurations, keyed by report type.
 * Each key represents a different report type (e.g., 'FASSOT'),
 * and its value is the corresponding ReportPageConfig.
 */
const reportConfigs: Record<string, ReportPageConfig> = {
  'REPORT1-PAGE0': {
    accountId: '571311412775',
    directoryAlias: 'fassot-reports',
    region: 'us-east-1',
    tabs: [
      {
        label: 'Programmatic Governance',
        id: 'programmaticgovernance',
        reports: [
          {
            headerText: 'Open SOX Deficiencies Reported - Running Total',
            dashboardId: 'aa10486c-0c81-401e-9a04-ab4ff1f17870',
          },
          {
            headerText: 'SOX Control Certification Compliance',
            dashboardId: 'c9257998-dc3c-417b-b5f1-3f64231b4717',
          },
          {
            headerText: 'Whiteboard Entries by Business and P&L Impact',
            dashboardId: '76837a68-c00d-4575-99e3-a37ae1165ae0',
          },
          {
            headerText: 'Internal Audit (IA) Findings and Action Items',
            dashboardId: 'c42bb9f8-a07b-4114-a0a5-3a78a20d19ee',
          },
        ],
      },
      {
        label: 'Controllership Automation Products, Tech and Science',
        id: 'controllershipautomationproductstechandscience',
        reports: [
          {
            headerText: 'SpendCentral',
            dashboardId: 'b82b4809-4fea-4ec0-8333-b5111df92055',
            renderAsButton: false,
          },
          {
            headerText: 'Business Architecture & Data Integration (BADI)',
            dashboardId: '27973a7b-8332-4b95-a8dd-56e8f608b77f',
            renderAsButton: true,
          },
        ],
      },
      {
        label: 'Procurement Controllership',
        id: 'procurementcontrollership',
        reports: [
          {
            headerText: 'Non-Inventory PO Miscode Defects',
            dashboardId: '01ed8ebf-0c90-47a9-8c37-ea8323b59d4c',
          },
          {
            headerText: 'Non-Inventory PO Receipt Defects',
            dashboardId: 'ed6718b9-7137-4d22-b6dd-21ff393bd981',
          },
        ],
      },
    ],
  },
  'REPORT2-FASSOT': {
    accountId: '571311412775',
    directoryAlias: 'fassot-reports',
    region: 'us-east-1',
    tabs: [
      {
        label: 'Asset Life Cycle',
        id: 'asset',
        reports: [
          { headerText: 'Asset Addition', dashboardId: '38fb979c-1665-4e14-92f0-f54ecf068086' },
          {
            headerText: 'Asset Reclassification',
            dashboardId: 'bbab6fe3-5c25-48de-8464-535376cdbbc3',
          },
          { headerText: 'Asset Transfer', dashboardId: '73a4ce9a-6651-4fb7-bbdf-ad1dd8d06e74' },
          { headerText: 'Asset Retirement', dashboardId: '31ca28d9-cb6d-4831-a443-bcaeef910490' },
        ],
      },
      {
        label: 'Financial Reports',
        id: 'financereports',
        reports: [
          { headerText: 'Reserve Subledger', dashboardId: '3726f476-9829-4fbf-a068-ef1d1d5b5bf5' },
          { headerText: 'Lantern PO Invoice', dashboardId: '38fb979c-1665-4e14-92f0-f54ecf068086' },
        ],
      },
      {
        label: 'CIP',
        id: 'cip',
        reports: [
          { headerText: 'CIP', dashboardId: '35dbfd49-3b57-4c10-bd1e-05ad92830994' },
          {
            headerText: 'CIP to Capitalization',
            dashboardId: 'b49205fa-d6bc-449d-a22c-70f6311ece47',
          },
        ],
      },
    ],
  },
  'REPORT3-RISKS': {
    accountId: '571311412775',
    directoryAlias: 'fassot-reports',
    region: 'us-east-1',
    reports: [
      {
        headerText: 'GRC Control Completion Metrics - Deep Dive',
        dashboardId: '9b4b7ba7-9479-4768-b737-da243dbaaef1',
      },
    ],
  },
  'REPORT4-PODEFECTS': {
    accountId: '571311412775',
    directoryAlias: 'fassot-reports',
    region: 'us-east-1',
    tabs: [
      {
        label: 'Fincontroller',
        id: 'fc',
        reports: [
          {
            headerText: 'COE Deep Dive',
            dashboardId: 'a3b0f6b7-82fc-4a69-805a-a3c9b3c1e17a',
          },
        ],
      },
    ],
  },
  'REPORT5-AUTO': {
    accountId: '571311412775',
    directoryAlias: 'fassot-reports',
    region: 'us-east-1',
    reports: [
      {
        headerText: 'WWAS Controls vs C-Hub Automated Controls',
        dashboardId: '8d72551a-8823-4f01-8b02-e04adcd238c4',
      },
      {
        headerText: 'Tracks Control Task status changes in real time',
        dashboardId: '5d6e011c-3b29-4166-8e5f-05669e239e32',
      },
    ],
  },
  'REPORT6-PO': {
    accountId: '571311412775',
    directoryAlias: 'fassot-reports',
    region: 'us-east-1',
    tabs: [
      {
        label: 'Page0 Metrics',
        id: '1',
        reports: [
          {
            headerText: 'Purchse Order Miscode Defects',
            dashboardId: '38fb979c-1665-4e14-92f0-f54ecf068086',
          },
          {
            headerText: 'Non-Inventory PO Receipts Defects',
            dashboardId: 'bbab6fe3-5c25-48de-8464-535376cdbbc3',
          },
        ],
      },
      {
        label: 'POAI',
        id: '2',
        reports: [
          { headerText: 'Past Due COEs', dashboardId: '3726f476-9829-4fbf-a068-ef1d1d5b5bf5' },
          {
            headerText: 'Past Due POAI Training',
            dashboardId: '38fb979c-1665-4e14-92f0-f54ecf068086',
          },
          {
            headerText: 'POAI - 1st Occurence',
            dashboardId: '38fb979c-1665-4e14-92f0-f54ecf068086',
          },
          {
            headerText: 'POAI - 2nd Occurence',
            dashboardId: '38fb979c-1665-4e14-92f0-f54ecf068086',
          },
          {
            headerText: 'POAI - 3rd Occurence',
            dashboardId: '38fb979c-1665-4e14-92f0-f54ecf068086',
          },
        ],
      },
      {
        label: 'PO Receiving',
        id: '3',
        reports: [
          {
            headerText: 'Under Received Purchase Orders',
            dashboardId: '35dbfd49-3b57-4c10-bd1e-05ad92830994',
          },
          {
            headerText: 'Purchase Orders Trending to be Overspent',
            dashboardId: 'b49205fa-d6bc-449d-a22c-70f6311ece47',
          },
          {
            headerText: 'Purchase Orders Over-Received',
            dashboardId: 'b49205fa-d6bc-449d-a22c-70f6311ece47',
          },
          {
            headerText: 'Dwelling Purchase Orders',
            dashboardId: 'b49205fa-d6bc-449d-a22c-70f6311ece47',
          },
          {
            headerText: 'IOH Notification',
            dashboardId: 'b49205fa-d6bc-449d-a22c-70f6311ece47',
          },
        ],
      },
      {
        label: 'PO Miscodes',
        id: '4',
        reports: [
          {
            headerText: 'Overall Purchase Order Receipt Defects',
            dashboardId: '35dbfd49-3b57-4c10-bd1e-05ad92830994',
          },
          {
            headerText: 'Non-Recurring Purchase Order Miscode Defects',
            dashboardId: 'b49205fa-d6bc-449d-a22c-70f6311ece47',
          },
        ],
      },
    ],
  },
  'REPORT7-CONTROL': {
    accountId: '571311412775',
    directoryAlias: 'fassot-reports',
    region: 'us-east-1',
    tabs: [
      {
        label: 'Control Efficiency',
        id: '1',
        reports: [
          {
            headerText: 'GRC - Control Completion Metrics - Deep Dive',
            dashboardId: '38fb979c-1665-4e14-92f0-f54ecf068086',
          },
          {
            headerText: 'Total number of control reviews within WWAS - Deep Dive',
            dashboardId: 'bbab6fe3-5c25-48de-8464-535376cdbbc3',
          },
        ],
      },
      {
        label: 'FinController',
        id: '2',
        reports: [
          {
            headerText: 'Whiteboard Entries Deep Dive',
            dashboardId: '3726f476-9829-4fbf-a068-ef1d1d5b5bf5',
          },
          { headerText: 'COE Deep Dive', dashboardId: 'a3b0f6b7-82fc-4a69-805a-a3c9b3c1e17a' },
        ],
      },
    ],
  },
  'REPORT8-CONTROLSPECIFIC': {
    accountId: '571311412775',
    directoryAlias: 'fassot-reports',
    region: 'us-east-1',
    tabs: [
      {
        label: 'GRC5257 (ARO)',
        id: '5',
        reports: [
          {
            headerText: 'GRC - Control Completion Metrics - Deep Dive',
            dashboardId: '9b4b7ba7-9479-4768-b737-da243dbaaef1',
          },
          {
            headerText: 'Control Execution Progress',
            dashboardId: 'b6917076-d66f-4b87-92ee-7aa99049eb61',
          },
        ],
      },
    ],
  },
};

export default reportConfigs;
